import { ActionIcon, Box, Group, Paper, Select, Stack, Text, TextInput, Title } from "@mantine/core";
import {
  ICheckItem,
  IChecklistItem,
  IChecklistItemRow,
  IStampSettings,
  TChecklistItemStatus,
} from "@mctypes/checklist.types";
import React, { useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi } from "ag-grid-community";
import { selectStartupChecklistQuestion } from "../../startupChecklistsSlice";
import classes from "./OverleveringChecklistReport.module.css";
import { IconMessageCircle, IconSearch, IconX } from "@tabler/icons-react";
import { useHistory, useLocation } from "react-router-dom";
import { useStore } from "react-redux";
import { IRootState } from "../../../../../store";
import { setHasUnsavedCommentInRedux } from "../../../../../global/globalSlice";
import MultiCommentMantine from "../../../issuesApp/IssuesTab/IssueItemModal/new/MultiCommentMantine";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import {
  IOverleveringChecklistQuestionRow,
  IOverleveringChecklistRow,
} from "@mcapps/shared/startupChecklists/templates/901-overlevering/901Schema";
import type { INivaa1Assessment } from "@mcapps/shared/startupChecklists/templates/902-forenklet-styring/902InitialData";
import StampCheck from "../../../checklist/StampCheck";
import type { DirectReportActions } from "../../../report/useDirectReportSave";
import { ensureOverleveringChecklistReport } from "./overleveringChecklistUtils";

const STARTUP_STAMP_SETTINGS: IStampSettings = {
  yes: { color: "#00975f", icon: "check", text: "JA" },
  no: { color: "#dc3545", icon: "close", text: "NEI" },
  ir: { color: "#f8992e", icon: "ban", text: "IR" },
  na: { color: "#0000004F", icon: "circle", text: "-" },
  uav: { color: "#4085b6ff", icon: "question", text: "Uavklart" },
};

const assessmentOptions: { value: INivaa1Assessment; label: string; description: string; color: string }[] = [
  { value: "RED", label: "Høy risiko", description: "Ikke utført/høy risiko. Behov for tiltak.", color: "#c92a2a" },
  {
    value: "YELLOW",
    label: "Middels risiko",
    description: "Middels risiko. Tiltak må utføres eller pågår.",
    color: "#e5ae00",
  },
  {
    value: "LIGHT_GREEN",
    label: "Ikke kommunisert",
    description: "Avklart/inkludert i relevante dokumenter, ikke kommunisert i oppdraget ennå.",
    color: "#7dc67b",
  },
  {
    value: "DARK_GREEN",
    label: "Avklart",
    description:
      "Avklart/inkludert i aktuelle dokumenter og kommunisert til relevante medarbeidere, eller det er ikke behov for avklaring, eller det er ikke spesielle krav.",
    color: "#237a45",
  },
  { value: "BLUE", label: "Mulighet", description: "Mulighet som kan brukes i oppdraget.", color: "#2775b6" },
  { value: "GRAY", label: "Ikke relevant", description: "Ikke relevant.", color: "#868e96" },
];

const toChecklistStatus = (answer: IOverleveringChecklistQuestionRow["answer"]): TChecklistItemStatus => {
  switch (answer) {
    case "JA":
      return "YES";
    case "NEI":
      return "NO";
    case "UAVKLART":
      return "UAV";
    case "IR":
      return "IR";
    default:
      return "N/A";
  }
};

const fromChecklistStatus = (status: TChecklistItemStatus): IOverleveringChecklistQuestionRow["answer"] => {
  switch (status) {
    case "UAV":
      return "UAVKLART";
    case "YES":
      return "JA";
    case "NO":
      return "NEI";
    case "IR":
      return "IR";
    default:
      return null;
  }
};

const answerLabel = (answer: IOverleveringChecklistQuestionRow["answer"]) =>
  ({ JA: "Ja", NEI: "Nei", IR: "Ikke relevant", UAVKLART: "Uavklart" }[answer] ?? "Ikke besvart");

const answerAttribution = (row: IOverleveringChecklistQuestionRow) => {
  if (!row.answerUpdatedBy) return "Ingen registrert informasjon om hvem som svarte";
  const date = row.answerUpdatedAt
    ? new Date(row.answerUpdatedAt).toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" })
    : "";
  return `${row.answer === null ? "Svar fjernet" : "Besvart"} av ${row.answerUpdatedBy.name}${
    date ? ` · ${date}` : ""
  }`;
};

const renderChecklistText = (text: string) => {
  const parts: React.ReactNode[] = [];
  const linkPattern = /\[([^\]]+)\]\((https:\/\/[^)\s]+)\)/g;
  let lastIndex = 0;
  for (const match of text.matchAll(linkPattern)) {
    const [markdown, label, href] = match;
    const start = match.index ?? 0;
    if (start > lastIndex) parts.push(text.slice(lastIndex, start));
    parts.push(
      <a
        key={`${start}:${href}`}
        className={classes.textLink}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => event.stopPropagation()}
      >
        {label}
      </a>,
    );
    lastIndex = start + markdown.length;
  }
  if (lastIndex === 0) return text;
  parts.push(text.slice(lastIndex));
  return parts;
};

const columns: ColDef<IOverleveringChecklistRow>[] = [
  { field: "nr", headerName: "Nr.", width: 72, minWidth: 72, maxWidth: 72, resizable: false },
  {
    field: "text",
    headerName: "Kontrollspørsmål / krav",
    flex: 1,
    minWidth: 320,
    wrapText: true,
    autoHeight: true,
    cellStyle: { lineHeight: "1.5", paddingTop: "12px", paddingBottom: "12px", whiteSpace: "pre-wrap" },
    cellRenderer: ({ data }) => (data ? <span>{renderChecklistText(data.text)}</span> : null),
    colSpan: ({ data }) => (data?.kind === "SECTION" ? 3 : 1),
  },
  {
    colId: "answer",
    getQuickFilterText: ({ data }) =>
      data?.kind === "QUESTION" ? (data.answer === "UAVKLART" ? "Uavklart" : data.answer) ?? "" : "",
    headerName: "Svar",
    width: 156,
    minWidth: 156,
    maxWidth: 156,
    resizable: false,
    cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    cellRenderer: ({ data, context }) =>
      data?.kind === "QUESTION" ? (
        <div
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key !== "ArrowUp" && event.key !== "ArrowDown") event.stopPropagation();
          }}
        >
          {context.renderAnswer(data)}
        </div>
      ) : null,
  },
  {
    colId: "comments",
    getQuickFilterText: ({ data }) =>
      data?.kind === "QUESTION"
        ? (data.comments ?? [])
            .filter((comment) => !comment.deleted)
            .map((comment) => comment.plainText ?? "")
            .join(" ")
        : "",
    headerName: "",
    width: 64,
    minWidth: 64,
    maxWidth: 64,
    resizable: false,
    headerTooltip: "Kommentarer",
    cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    cellRenderer: ({ data }) => {
      const count = data?.kind === "QUESTION" ? data.comments?.filter((comment) => !comment.deleted).length ?? 0 : 0;
      return count ? (
        <Group gap={4} wrap="nowrap" title={`${count} kommentarer`}>
          <IconMessageCircle size={14} />
          <Text size="sm">{count}</Text>
        </Group>
      ) : null;
    },
  },
];

const OverleveringChecklistReport: React.FC<{
  data: { rows: IOverleveringChecklistRow[] } | null;
  directSave: DirectReportActions;
  assessmentEnabled?: boolean;
}> = ({ data: rawEditData, directSave, assessmentEnabled = false }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const syncedUrl = useRef<string | null>(null);
  const [search, setSearch] = useState("");
  const store = useStore<IRootState>();
  const user = useAppSelector((state) => state.globalReducer.user);
  const project = useAppSelector((state) => state.adminReducer.project);
  const reportId = useAppSelector((state) => state.reportReducer.currentReportId);
  const selectedQuestionId = useAppSelector(
    (state) => state.startupChecklistsReducer.selectedQuestionByReport[reportId] ?? null,
  );
  const gridApi = useRef<GridApi<IOverleveringChecklistRow> | null>(null);
  const syncSelection = () =>
    gridApi.current?.forEachNode((node) =>
      node.setSelected(node.data?.kind === "QUESTION" && node.data.questionId === selectedQuestionId),
    );
  const revealSelection = () => {
    syncSelection();
    if (selectedQuestionId) {
      const node = gridApi.current?.getRowNode(selectedQuestionId);
      if (node && node.rowIndex != null) gridApi.current.ensureNodeVisible(node);
    }
  };
  useEffect(revealSelection, [selectedQuestionId]);

  useEffect(() => {
    if (!reportId || !rawEditData) return;
    const urlKey = `${reportId}:${location.search}`;
    const params = new URLSearchParams(location.search);
    if (syncedUrl.current !== urlKey) {
      syncedUrl.current = urlKey;
      const requested = params.get("question");
      const questions = rawEditData.rows.filter((row) => row.kind === "QUESTION");
      const nextId =
        questions.find((row) => row.questionId === requested)?.questionId ?? questions[0]?.questionId ?? null;
      if (nextId) setSearch("");
      if (nextId !== selectedQuestionId) {
        dispatch(selectStartupChecklistQuestion({ reportId, questionId: nextId }));
        return;
      }
    }
    if (selectedQuestionId) params.set("question", selectedQuestionId);
    else params.delete("question");
    const nextSearch = params.toString() ? `?${params.toString()}` : "";
    if (nextSearch !== location.search) {
      syncedUrl.current = `${reportId}:${nextSearch}`;
      history.replace({ ...location, search: nextSearch });
    }
  }, [reportId, rawEditData, selectedQuestionId, location, history, dispatch]);

  const editData = rawEditData == null ? null : ensureOverleveringChecklistReport(rawEditData);

  if (!editData) return null;

  const questionPath = (questionId: string) => {
    const index = rawEditData.rows.findIndex((row) => row.kind === "QUESTION" && row.questionId === questionId);
    if (index < 0) throw new Error("Spørsmålet finnes ikke");
    return `rows[${index}]`;
  };

  function selectQuestion(questionId: string) {
    if (questionId === selectedQuestionId) return true;
    if (directSave.saving) return false;
    if (
      store.getState().globalReducer.hasUnsavedComment &&
      !window.confirm("Du har en usendt kommentar. Vil du forkaste den?")
    )
      return false;
    dispatch(setHasUnsavedCommentInRedux(false));
    dispatch(selectStartupChecklistQuestion({ reportId, questionId }));
    return true;
  }

  const assessmentCell = (row: IOverleveringChecklistQuestionRow) => {
    const assessment = (row as IOverleveringChecklistQuestionRow & { assessment?: INivaa1Assessment | null })
      .assessment;
    const selected = assessmentOptions.find((option) => option.value === assessment);
    return (
      <div
        data-checklist-assessment
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        style={{ width: "100%" }}
      >
        <Select
          aria-label={`Vurdering for spørsmål ${row.nr}`}
          title={selected?.label ?? "Velg vurdering"}
          placeholder="Velg vurdering"
          size="xs"
          clearable
          disabled={directSave.disabled || directSave.saving}
          value={assessment ?? null}
          data={assessmentOptions.map(({ value, label }) => ({ value, label }))}
          comboboxProps={{ width: 440, withinPortal: true, position: "bottom-end" }}
          styles={{ dropdown: { maxWidth: "calc(100vw - 32px)" } }}
          leftSection={
            selected ? (
              <Box aria-hidden style={{ width: 12, height: 12, backgroundColor: selected.color, borderRadius: 2 }} />
            ) : null
          }
          renderOption={({ option }) => {
            const item = assessmentOptions.find(({ value }) => value === option.value);
            return item ? (
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <Box
                  aria-hidden
                  style={{
                    flexShrink: 0,
                    width: 12,
                    height: 12,
                    marginTop: 4,
                    backgroundColor: item.color,
                    borderRadius: 2,
                  }}
                />
                <Text size="xs" style={{ whiteSpace: "normal" }}>
                  {item.description}
                </Text>
              </Group>
            ) : null;
          }}
          onChange={(value) => {
            void directSave.updateField(`${questionPath(row.questionId)}.assessment`, value).catch(() => undefined);
          }}
        />
      </div>
    );
  };

  const assessmentColumn: ColDef<IOverleveringChecklistRow> = {
    colId: "assessment",
    headerName: "Vurdering",
    width: 180,
    minWidth: 180,
    resizable: false,
    cellRendererParams: { suppressMouseEventHandling: () => true },
    cellStyle: { display: "flex", alignItems: "center" },
    cellRenderer: ({ data }) => (data?.kind === "QUESTION" ? assessmentCell(data) : null),
  };

  const answerCell = (row: IOverleveringChecklistQuestionRow) => {
    const checkItem: ICheckItem = {
      status: toChecklistStatus(row.answer),
      updatedBy: null,
      comment: {
        createdBy: null,
        text: "",
        createdDate: null,
      },
    };

    const originalItem = {} as IChecklistItem;
    const checklistRow = {
      _id: row.questionId,
      originalItem,
    } as IChecklistItemRow;

    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <StampCheck
          disabled={!row.questionId || directSave.disabled}
          row={checklistRow}
          checkItem={checkItem}
          stampSettings={STARTUP_STAMP_SETTINGS}
          includeIr={true}
          includeUavOnDefaultRoles={true}
          statusDisabled={true}
          toggle={(_, __, newValue) => {
            void directSave
              .updateField(`${questionPath(row.questionId)}.answer`, fromChecklistStatus(newValue))
              .catch(() => undefined); // The report shows the save error and a retry action.
          }}
        />
      </div>
    );
  };

  const selectedQuestion = editData.rows.find(
    (row): row is IOverleveringChecklistQuestionRow => row.kind === "QUESTION" && row.questionId === selectedQuestionId,
  );
  return (
    <Stack px="md" py="sm" gap="sm" className={classes.root}>
      <TextInput
        aria-label="Søk i sjekklisten"
        placeholder="Søk i sjekklisten…"
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
        leftSection={<IconSearch size={16} />}
        rightSection={
          search ? (
            <ActionIcon variant="subtle" color="gray" aria-label="Tøm søk" onClick={() => setSearch("")}>
              <IconX size={16} />
            </ActionIcon>
          ) : null
        }
        maw={420}
      />
      <div className={classes.layout}>
        <Box
          className="ag-theme-quartz"
          style={{ minWidth: 0, height: "100%" }}
          onClickCapture={(event) => {
            if (!(event.target instanceof Element)) return;
            const rowId = event.target.closest(".ag-row[row-id]")?.getAttribute("row-id");
            const row = rowId == null ? null : gridApi.current?.getRowNode(rowId)?.data;
            if (row?.kind === "QUESTION" && !selectQuestion(row.questionId)) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
        >
          <AgGridReact<IOverleveringChecklistRow>
            theme="legacy"
            rowData={editData.rows}
            quickFilterText={search}
            overlayNoRowsTemplate="Ingen spørsmål matcher søket"
            columnDefs={assessmentEnabled ? [columns[0], columns[1], assessmentColumn, columns[3]] : columns}
            context={{ renderAnswer: answerCell, assessmentEnabled }}
            defaultColDef={{ sortable: false, resizable: true, suppressMovable: true, suppressHeaderMenuButton: true }}
            getRowId={({ data }) => (data.kind === "QUESTION" ? data.questionId : `section:${data.nr}:${data.text}`)}
            rowSelection={{
              mode: "singleRow",
              checkboxes: false,
              enableClickSelection: false,
              isRowSelectable: (node) => node.data?.kind === "QUESTION",
            }}
            onGridReady={({ api }) => {
              gridApi.current = api;
              syncSelection();
            }}
            onRowDataUpdated={syncSelection}
            onFirstDataRendered={revealSelection}
            navigateToNextCell={({ key, previousCellPosition, nextCellPosition, api }) => {
              if (key !== "ArrowUp" && key !== "ArrowDown") return nextCellPosition;
              const direction = key === "ArrowDown" ? 1 : -1;
              for (
                let index = previousCellPosition.rowIndex + direction;
                index >= 0 && index < api.getDisplayedRowCount();
                index += direction
              ) {
                const row = api.getDisplayedRowAtIndex(index)?.data;
                if (row?.kind !== "QUESTION") continue;
                if (!selectQuestion(row.questionId)) return previousCellPosition;
                api.ensureIndexVisible(index);
                return { ...previousCellPosition, rowIndex: index };
              }
              return previousCellPosition;
            }}
            onCellKeyDown={(event) => {
              if (event.data?.kind === "QUESTION" && (event.event as KeyboardEvent)?.key === "Enter")
                selectQuestion(event.data.questionId);
            }}
            getRowClass={({ data }) => (data?.kind === "SECTION" ? classes.sectionRow : undefined)}
            getRowStyle={({ data }) =>
              data?.kind === "SECTION" ? { background: "var(--mantine-color-gray-1)" } : undefined
            }
            animateRows={false}
            headerHeight={38}
            rowHeight={56}
          />
        </Box>
        <Paper withBorder p="sm" className={classes.details}>
          {!selectedQuestion && <Text c="dimmed">Velg et spørsmål for å se detaljer og kommentarer.</Text>}
          {selectedQuestion && (
            <Stack gap="lg">
              <Text size="sm" c="dimmed">
                Spørsmål {selectedQuestion.nr}
              </Text>
              <Title size="h5" fw={400}>
                {renderChecklistText(selectedQuestion.text)}
              </Title>
              {assessmentEnabled && (
                <Group justify="space-between" wrap="nowrap">
                  <Text size="sm">Vurdering</Text>
                  <Box w={180}>{assessmentCell(selectedQuestion)}</Box>
                </Group>
              )}
              {!assessmentEnabled && (
                <Group justify="space-between">
                  <Text size="sm">Svar</Text>
                  {answerCell(selectedQuestion)}
                </Group>
              )}
              {!assessmentEnabled &&
                (selectedQuestion.answer != null ||
                  selectedQuestion.answerUpdatedBy != null ||
                  (selectedQuestion.answerHistory?.length ?? 0) > 0) && (
                  <Stack gap={4}>
                    <Text size="xs" c="dimmed">
                      {answerAttribution(selectedQuestion)}
                    </Text>
                    {(selectedQuestion.answerHistory?.length ?? 0) > 0 && (
                      <details key={`history:${selectedQuestion.questionId}`} className={classes.history}>
                        <summary>Vis svarhistorikk · {selectedQuestion.answerHistory.length}</summary>
                        <Stack gap="xs" mt="xs">
                          {[...selectedQuestion.answerHistory].reverse().map((change) => (
                            <div key={change._id}>
                              <Text size="xs">
                                <Text span fw={500}>
                                  {change.changedBy.name}
                                </Text>{" "}
                                · {answerLabel(change.from)} → {answerLabel(change.to)}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {new Date(change.changedAt).toLocaleString("nb-NO", {
                                  dateStyle: "short",
                                  timeStyle: "short",
                                })}
                              </Text>
                            </div>
                          ))}
                        </Stack>
                      </details>
                    )}
                  </Stack>
                )}
              <Title order={4} size="h5">
                Kommentarer
              </Title>
              <fieldset disabled={directSave.disabled} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                <MultiCommentMantine
                  key={selectedQuestion.questionId}
                  comments={selectedQuestion.comments ?? []}
                  loggedInUser={user}
                  members={project?.members ?? []}
                  alwaysShowInput
                  showCancelButton={false}
                  useBrowserPrompt
                  sortDesc
                  noCommentsText="Ingen kommentarer ennå."
                  onAdd={(text, metadata) =>
                    directSave.updateComment({
                      path: `${questionPath(selectedQuestionId)}.comments`,
                      operation: "add",
                      text,
                      plainText: metadata?.plainText ?? "",
                    })
                  }
                  onUpdate={(commentId, text, metadata) =>
                    directSave.updateComment({
                      path: `${questionPath(selectedQuestionId)}.comments`,
                      operation: "edit",
                      commentId,
                      text,
                      plainText: metadata?.plainText ?? "",
                    })
                  }
                  onDelete={(commentId) => {
                    void directSave
                      .updateComment({
                        path: `${questionPath(selectedQuestionId)}.comments`,
                        operation: "delete",
                        commentId,
                      })
                      .catch(() => undefined);
                  }}
                />
              </fieldset>
            </Stack>
          )}
        </Paper>
      </div>
    </Stack>
  );
};

export default OverleveringChecklistReport;
